import { supabase } from '@/lib/supabase';
import type { AdminAuditLog } from '@/types';

export const adminAuditApi = {
  list: async (params: { orgId?: string } = {}): Promise<AdminAuditLog[]> => {
    let query = supabase
      .from('admin_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (params.orgId) {
      query = query.eq('target_org_id', params.orgId);
    }

    const { data, error } = await query;
    if (error) throw error;

    const rows = (data ?? []) as AdminAuditLog[];
    if (!rows.length) return [];

    const orgIds = [...new Set(rows.map((row) => row.target_org_id).filter(Boolean))] as string[];
    const userIds = [...new Set(rows.map((row) => row.target_user_id).filter(Boolean))] as string[];

    const [orgsResult, usersResult] = await Promise.all([
      orgIds.length
        ? supabase.from('organizations').select('id, name').in('id', orgIds)
        : Promise.resolve({ data: [], error: null }),
      userIds.length
        ? supabase.from('users').select('id, email, first_name, last_name').in('id', userIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (orgsResult.error) throw orgsResult.error;
    if (usersResult.error) throw usersResult.error;

    const orgById = new Map(
      (orgsResult.data ?? []).map((org: { id: string; name: string }) => [org.id, org])
    );
    const userById = new Map(
      (
        usersResult.data ?? []
      ).map((user: { id: string; email: string | null; first_name: string | null; last_name: string | null }) => [
        user.id,
        user,
      ])
    );

    return rows.map((row) => ({
      ...row,
      metadata:
        row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
          ? row.metadata
          : {},
      target_org: row.target_org_id ? orgById.get(row.target_org_id) ?? null : null,
      target_user: row.target_user_id ? userById.get(row.target_user_id) ?? null : null,
    }));
  },
};
